import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import block from 'bem-cn';

import Button from 'components/Button';
import SVG from 'components/SVG';
import Spinner from 'components/Spinner/desktop';

import useSurvey, { SURVEY_SCREENS } from 'features/survey/model/useSurvey';

import crossSVG from 'shared/img/cross-bold.svg';
import checkSVG from 'shared/img/check.svg';

import './SurveyModal.scss';

const b = block('survey-modal');

const SurveyModal = ({ isMobile }) => {
  const model = useSurvey();
  const { locale, isModalOpen, handleClose } = model;

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, handleClose]);

  if (!isModalOpen) return null;

  const renderStatic = (title, texts, buttonText, buttonCallback) => (
    <div className={b('static')}>
      <div className={b('title')}>{title}</div>
      <div className={b('text')}>
        {texts.map(text => (
          <p key={text}>{text}</p>
        ))}
      </div>
      <div className={b('static-button')}>
        <Button text={buttonText} size="low" upperCased callBack={buttonCallback} />
      </div>
    </div>
  );

  const renderQuestion = () => {
    if (!model.question) return null;

    return (
      <div className={b('question')}>
        <div className={b('progress')}>
          <div className={b('progress-track')}>
            <div
              className={b('progress-fill')}
              style={{ width: `${(model.questionNumber / model.questionsCount) * 100}%` }}
            />
          </div>
          <div className={b('progress-label')}>{`${model.questionNumber}/${model.questionsCount}`}</div>
        </div>
        <div className={b('question-title')}>{model.question.text}</div>
        <div className={b('options')}>
          {model.question.choices.map(choice => {
            const isSelected = model.selected.includes(choice.id);

            return (
              <button
                key={choice.id}
                type="button"
                className={b('option')}
                onClick={() => model.toggleChoice(choice.id)}>
                <span className={b('option-label')}>{choice.text}</span>
                <span className={b('option-checkbox')}>
                  {isSelected && <SVG className={b('option-check').toString()} svgProps={{ svg: checkSVG }} />}
                </span>
              </button>
            );
          })}
        </div>
        <div className={b('buttons')}>
          <div className={b('button')}>
            <Button
              text={locale.surveyNext}
              size="low"
              disabled={!model.selected.length || model.actionProcessing}
              callBack={model.handleNext}
            />
          </div>
        </div>
      </div>
    );
  };

  const layout = (
    <div className={b()} onClick={handleClose} role="presentation">
      <div className={b('card', { isMobile })} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <Spinner isLoading={model.actionProcessing} />
        <div className={b('header')}>
          <button className={b('close')} type="button" onClick={handleClose}>
            <SVG className={b('close-icon').toString()} svgProps={{ svg: crossSVG }} />
          </button>
        </div>
        <div className={b('content')}>
          {model.screen === SURVEY_SCREENS.INTRO &&
            renderStatic(
              locale.surveyIntroTitle,
              [locale.surveyIntroText1, locale.surveyIntroText2],
              locale.surveyStart,
              model.handleStart,
            )}
          {model.screen === SURVEY_SCREENS.QUESTION && renderQuestion()}
          {model.screen === SURVEY_SCREENS.THANKS &&
            renderStatic(locale.surveyThanksTitle, [locale.surveyThanksText], locale.surveyFinish, handleClose)}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(layout, document.getElementById('modal-root'));
};

export default SurveyModal;
